import React, { useState, useCallback } from 'react';
import { View, StyleSheet, FlatList, TouchableOpacity, RefreshControl, Linking } from 'react-native';
import { Text, Card, ActivityIndicator, Divider, Badge } from 'react-native-paper';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import api from '../../services/api';

const RepartidorScreen = () => {
  const navigation = useNavigation();
  const [preparados, setPreparados] = useState([]);
  const [enRuta, setEnRuta] = useState([]);
  const [entregados, setEntregados] = useState([]);
  const [resumen, setResumen] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState('preparados');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [prep, ruta, entreg, res] = await Promise.all([
        api.getRepartidorPreparados(),
        api.getRepartidorEnRuta(),
        api.getRepartidorEntregados(),
        api.getRepartidorResumen(),
      ]);
      setPreparados(prep);
      setEnRuta(ruta);
      setEntregados(entreg);
      setResumen(res);
    } catch (error) {
      console.error('Error al cargar datos de repartidor:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      loadData();
    }, [loadData])
  );

  const formatFecha = (fecha) => {
    if (!fecha) return '';
    const date = new Date(fecha);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) {
      return `Hoy ${date.toLocaleTimeString('es-HN', { hour: '2-digit', minute: '2-digit' })}`;
    }
    return date.toLocaleDateString('es-HN', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
    });
  };

  const formatCurrency = (value) => `L. ${parseFloat(value || 0).toFixed(2)}`;

  const handleLlamarCliente = (telefono) => {
    if (telefono) {
      Linking.openURL(`tel:${telefono}`);
    }
  };

  const renderResumen = () => (
    <View style={styles.resumenContainer}>
      <View style={[styles.resumenCard, { backgroundColor: '#D1ECF1' }]}>
        <Ionicons name="cube" size={22} color="#0C5460" />
        <Text style={[styles.resumenNumero, { color: '#0C5460' }]}>{resumen.preparados || 0}</Text>
        <Text style={styles.resumenLabel}>Por Recoger</Text>
      </View>
      <View style={[styles.resumenCard, { backgroundColor: '#E8D5F5' }]}>
        <Ionicons name="car" size={22} color="#6F42C1" />
        <Text style={[styles.resumenNumero, { color: '#6F42C1' }]}>{resumen.en_ruta || 0}</Text>
        <Text style={styles.resumenLabel}>En Ruta</Text>
      </View>
      <View style={[styles.resumenCard, { backgroundColor: '#D4EDDA' }]}>
        <Ionicons name="checkmark-circle" size={22} color="#155724" />
        <Text style={[styles.resumenNumero, { color: '#155724' }]}>{resumen.entregados_hoy || 0}</Text>
        <Text style={styles.resumenLabel}>Entregados</Text>
      </View>
      <View style={[styles.resumenCard, { backgroundColor: '#FFF3CD' }]}>
        <Ionicons name="cash" size={22} color="#856404" />
        <Text style={[styles.resumenNumero, { color: '#856404', fontSize: 16 }]}>
          {formatCurrency(resumen.total_entregado_hoy)}
        </Text>
        <Text style={styles.resumenLabel}>Total Hoy</Text>
      </View>
    </View>
  );

  const getEstadoConfig = (estado) => {
    const configs = {
      preparado: { color: '#17A2B8', icon: 'cube', label: 'LISTO' },
      en_ruta: { color: '#6F42C1', icon: 'car', label: 'EN RUTA' },
      entregado: { color: '#28A745', icon: 'checkmark-circle', label: 'ENTREGADO' },
      completada: { color: '#28A745', icon: 'checkmark-done-circle', label: 'COMPLETADA' },
    };
    return configs[estado] || { color: '#999', icon: 'help', label: estado };
  };

  const renderPedidoItem = ({ item }) => {
    const estadoConfig = getEstadoConfig(item.estado);

    return (
      <TouchableOpacity
        onPress={() => navigation.navigate('DetalleEntrega', { pedido: item })}
      >
        <Card style={styles.pedidoCard}>
          <Card.Content>
            <View style={styles.pedidoHeader}>
              <View style={styles.pedidoInfo}>
                <View style={styles.pedidoIdRow}>
                  <Text style={styles.pedidoId}>Pedido #{item.id}</Text>
                  <View style={[styles.estadoChip, { backgroundColor: estadoConfig.color }]}>
                    <Ionicons name={estadoConfig.icon} size={12} color="#fff" />
                    <Text style={styles.estadoText}> {estadoConfig.label}</Text>
                  </View>
                </View>
                <Text style={styles.pedidoCliente}>
                  <Ionicons name="person" size={14} color="#666" /> {item.cliente_nombre || 'Sin cliente'}
                </Text>
                {item.cliente_direccion && (
                  <Text style={styles.pedidoDireccion} numberOfLines={1}>
                    <Ionicons name="location" size={14} color="#6F42C1" /> {item.cliente_direccion}
                  </Text>
                )}
              </View>
              <View style={styles.pedidoRight}>
                <Text style={styles.pedidoTotal}>{formatCurrency(item.total)}</Text>
                <Text style={styles.pedidoItems}>
                  {parseInt(item.total_productos || item.total_items || 0)} items
                </Text>
                {item.cliente_telefono && (
                  <TouchableOpacity
                    style={styles.llamarBtn}
                    onPress={(e) => { e.stopPropagation(); handleLlamarCliente(item.cliente_telefono); }}
                  >
                    <Ionicons name="call" size={16} color="#28A745" />
                  </TouchableOpacity>
                )}
              </View>
            </View>

            <Divider style={styles.divider} />

            <View style={styles.pedidoFooter}>
              <Text style={styles.pedidoFecha}>
                <Ionicons name="calendar" size={12} color="#888" /> {formatFecha(item.fecha)}
              </Text>
              {tab === 'preparados' && (
                <Text style={styles.tapText}>
                  Toca para recoger <Ionicons name="arrow-forward" size={12} color="#17A2B8" />
                </Text>
              )}
              {tab === 'en_ruta' && (
                <Text style={styles.tapText}>
                  Toca para entregar <Ionicons name="arrow-forward" size={12} color="#6F42C1" />
                </Text>
              )}
              {tab === 'entregados' && item.metodo_pago && (
                <View style={styles.metodoPagoChip}>
                  <Ionicons name={item.metodo_pago === 'efectivo' ? 'cash' : 'card'} size={12} color="#666" />
                  <Text style={styles.metodoPagoText}> {item.metodo_pago}</Text>
                </View>
              )}
            </View>
          </Card.Content>
        </Card>
      </TouchableOpacity>
    );
  };

  if (loading && !refreshing) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#6F42C1" />
        <Text style={styles.loadingText}>Cargando panel de entregas...</Text>
      </View>
    );
  }

  const dataMap = { preparados, en_ruta: enRuta, entregados };
  const dataToShow = dataMap[tab] || [];

  return (
    <View style={styles.container}>
      {renderResumen()}

      <View style={styles.tabsContainer}>
        <TouchableOpacity
          style={[styles.tab, tab === 'preparados' && styles.tabActive]}
          onPress={() => setTab('preparados')}
        >
          <Ionicons name="cube" size={16} color={tab === 'preparados' ? '#17A2B8' : '#999'} />
          <Text style={[styles.tabText, tab === 'preparados' && styles.tabTextActive]}>
            Por Recoger ({preparados.length})
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, tab === 'en_ruta' && styles.tabActive]}
          onPress={() => setTab('en_ruta')}
        >
          <Ionicons name="car" size={16} color={tab === 'en_ruta' ? '#6F42C1' : '#999'} />
          <Text style={[styles.tabText, tab === 'en_ruta' && styles.tabTextActive]}>
            En Ruta ({enRuta.length})
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, tab === 'entregados' && styles.tabActive]}
          onPress={() => setTab('entregados')}
        >
          <Ionicons name="checkmark-done" size={16} color={tab === 'entregados' ? '#28A745' : '#999'} />
          <Text style={[styles.tabText, tab === 'entregados' && styles.tabTextActive]}>
            Entregados ({entregados.length})
          </Text>
        </TouchableOpacity>
      </View>

      <FlatList
        data={dataToShow}
        keyExtractor={(item) => item.id.toString()}
        renderItem={renderPedidoItem}
        contentContainerStyle={styles.listContainer}
        ItemSeparatorComponent={() => <View style={{ height: 8 }} />}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); loadData(); }} />
        }
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Ionicons
              name={tab === 'en_ruta' ? 'car-outline' : tab === 'preparados' ? 'cube-outline' : 'checkmark-done-circle-outline'}
              size={64}
              color="#ccc"
            />
            <Text style={styles.emptyText}>
              {tab === 'en_ruta' ? 'No tienes pedidos en ruta' :
               tab === 'preparados' ? 'No hay pedidos listos para recoger' :
               'No has entregado pedidos hoy'}
            </Text>
          </View>
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingText: { marginTop: 10, color: '#666' },
  resumenContainer: { flexDirection: 'row', padding: 8, justifyContent: 'space-between' },
  resumenCard: { flex: 1, alignItems: 'center', padding: 10, borderRadius: 8, marginHorizontal: 3, elevation: 1 },
  resumenNumero: { fontSize: 20, fontWeight: 'bold', marginTop: 2 },
  resumenLabel: { fontSize: 9, color: '#666', marginTop: 2, textAlign: 'center' },
  tabsContainer: { flexDirection: 'row', paddingHorizontal: 8, marginBottom: 8 },
  tab: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 10, borderBottomWidth: 2, borderBottomColor: 'transparent' },
  tabActive: { borderBottomColor: '#6F42C1' },
  tabText: { fontSize: 12, color: '#999', marginLeft: 4 },
  tabTextActive: { color: '#333', fontWeight: 'bold' },
  listContainer: { padding: 8 },
  pedidoCard: { elevation: 2 },
  pedidoHeader: { flexDirection: 'row', justifyContent: 'space-between' },
  pedidoInfo: { flex: 1, marginRight: 10 },
  pedidoIdRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 4 },
  pedidoId: { fontSize: 16, fontWeight: 'bold', color: '#333', marginRight: 8 },
  estadoChip: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 12 },
  estadoText: { fontSize: 10, fontWeight: 'bold', color: '#fff' },
  pedidoCliente: { fontSize: 13, color: '#666', marginBottom: 2 },
  pedidoDireccion: { fontSize: 12, color: '#6F42C1', marginBottom: 2 },
  pedidoRight: { alignItems: 'flex-end' },
  pedidoTotal: { fontSize: 16, fontWeight: 'bold', color: '#28a745' },
  pedidoItems: { fontSize: 12, color: '#888', marginTop: 2 },
  llamarBtn: { marginTop: 6, padding: 6, backgroundColor: '#E8F5E9', borderRadius: 20 },
  divider: { marginVertical: 8 },
  pedidoFooter: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  pedidoFecha: { fontSize: 12, color: '#888' },
  tapText: { fontSize: 12, color: '#6F42C1', fontWeight: '500' },
  metodoPagoChip: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#F0F0F0', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10 },
  metodoPagoText: { fontSize: 11, color: '#666', textTransform: 'capitalize' },
  emptyContainer: { justifyContent: 'center', alignItems: 'center', paddingVertical: 50 },
  emptyText: { marginTop: 10, color: '#888', fontSize: 16, textAlign: 'center' },
});

export default RepartidorScreen;
